import { Directive, Injector, OnDestroy, OnInit } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';

/**
 * Directive that automatically marks a form control as dirty and touched when its value changes.
 *
 * This is useful for ControlValueAccessor components that don't automatically set
 * the dirty state when their internal values change. Without this directive, form
 * controls bound to CVAs will only be marked as "touched" but not "dirty" when values change,
 * preventing proper form validation and change detection.
 *
 * Usage:
 * ```html
 * <custom-input formControlName="myControl" trackDirtyOnChange></custom-input>
 * ```
 *
 * @example
 * // In a form with a custom ControlValueAccessor component
 * <shared-parameters-editor
 *   formControlName="parametersValues"
 *   trackDirtyOnChange
 *   [parametersDefinition]="parameters" />
 */
@Directive({
  selector: '[trackDirtyOnChange]',
  standalone: false,
})
export class TrackDirtyOnChangeDirective implements OnInit, OnDestroy {
  private subscription: Subscription | null = null;

  constructor(private injector: Injector) {}

  ngOnInit(): void {
    try {
      // Use Injector.get to avoid circular dependency issues
      // Cannot use @Self() @Optional() NgControl injection because it creates
      // a circular dependency when the component provides NG_VALUE_ACCESSOR
      const ngControl = this.injector.get(NgControl, null);

      if (ngControl?.control) {
        this.subscription = ngControl.control.valueChanges.subscribe(() => {
          // Mark as both dirty and touched for proper change detection
          // This ensures the parent form properly detects changes from ControlValueAccessor components
          if (!ngControl.control.dirty) {
            ngControl.control.markAsDirty();
          }
          if (!ngControl.control.touched) {
            ngControl.control.markAsTouched();
          }
        });
      }
    } catch (error) {
      console.warn('TrackDirtyOnChangeDirective: NgControl not found or error occurred.', error);
    }
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
